import { Address } from "@bluebottlecoffee/design-system/components/lib/types";
import { RecipientInfo } from "@bluebottlecoffee/design-system/components";
import emailIsValid from "./strings/email-is-valid";

export type ErrorFields = Omit<Address, "id" | "default"> & {
  email: string;
  giftNote?: string;
};

export type RecipientAddressValidationReturn = {
  isValid: boolean;
  errors?: ErrorFields;
  /**
   * For UI that can only handle one error at a time, return the first error
   */
  firstError?: string;
};

export type AddressValidationCopy = {
  isRequired?: string;
  inputTooLong?: string;
  emailFormatInvalid?: string;
  usPostalCodeFormatInvalid?: string;
  countryCodeFormatInvalid?: string;
  giftNoteTooLong?: string;
};

/**
 * Takes in an address with required name and email fields and an optional gift
 * note field and validates required fields, formatting, and string length
 */
export const recipientAddressValidation = (
  info: RecipientInfo,
  copy?: AddressValidationCopy,
  region?: string,
): RecipientAddressValidationReturn => {
  const {
    email,
    address: {
      name,
      street,
      extended,
      locality,
      district,
      postalCode,
      countryCode,
    },
    giftNote,
  } = info;
  const {
    isRequired = "is required",
    inputTooLong = "should be less than 45 characters",
    emailFormatInvalid = "Please enter a valid email address",
    usPostalCodeFormatInvalid = "US Postal code should be 5 digits",
    countryCodeFormatInvalid = "Country code should be 2 letters",
    giftNoteTooLong = "should be less than 250 characters",
  } = copy;
  const regionCode = region ?? "us";

  // Trim whitespace from all required fields
  const requiredFields = {
    email: email?.trim(),
    name: name?.trim(),
    street: street?.trim(),
    locality: locality?.trim(),
    district: district?.trim(),
    postalCode: postalCode?.trim(),
    countryCode: countryCode?.trim(),
  };

  // Trim whitespace from all text input fields
  const textInputFields = { ...requiredFields, extended: extended?.trim() };

  // Initialize empty errors object
  let errors: ErrorFields = {
    email: "",
    name: "",
    street: "",
    extended: "",
    locality: "",
    district: "",
    postalCode: "",
    countryCode: "",
    giftNote: "",
  };

  // Required field validation
  Object.entries(requiredFields).map(([key, value]) => {
    if (value?.length === 0) {
      errors = { ...errors, [key]: `${key} ${isRequired}` };
    }
    return null;
  });

  // Short field length validation
  Object.entries(textInputFields).map(([key, value]) => {
    if (value?.length > 45) {
      errors = { ...errors, [key]: `${key} ${inputTooLong}` };
    }
    return null;
  });

  // Email format validation
  if (requiredFields.email && !emailIsValid(requiredFields.email)) {
    errors = {
      ...errors,
      email: emailFormatInvalid,
    };
  }

  // If US Postal Code 5 digits validation
  if (
    requiredFields.postalCode &&
    regionCode === "us" &&
    !/^\d{5}$/.test(requiredFields.postalCode)
  ) {
    errors = {
      ...errors,
      postalCode: usPostalCodeFormatInvalid,
    };
  }

  // Country Code 2 letters
  if (
    requiredFields.countryCode &&
    !/^[A-Za-z]{2}$/.test(requiredFields.countryCode)
  ) {
    errors = {
      ...errors,
      countryCode: countryCodeFormatInvalid,
    };
  }

  // Gift Note length validation
  if (giftNote.length > 250) {
    errors = {
      ...errors,
      giftNote: giftNoteTooLong,
    };
  }

  // Check if there are any errors
  const isValid =
    Object.values(errors)
      .map((v) => v?.trim())
      .join("").length === 0;

  const firstError = Object.values(errors).find((v) => v?.trim().length > 0);

  // Return isValid boolean and any errors
  return {
    isValid,
    errors,
    firstError,
  };
};
